import { useEffect, useRef, useState } from "react";

/**
 * Self-typing terminal that loops through capture/recall demonstration.
 * Pure visual - no backend.
 */
const SCRIPT = [
  { kind: "prompt", text: "$ pip install mnemonic-sdk" },
  { kind: "out", text: "Successfully installed mnemo-0.1.0", tone: "green" },
  { kind: "prompt", text: "$ python agent.py" },
  { kind: "code", text: "from mnemo import Mnemo" },
  { kind: "code", text: "m = Mnemo(api_key=os.getenv('MNEMO_KEY'))" },
  { kind: "comment", text: "# 1. recall before the agent runs" },
  { kind: "code", text: "ctx = m.recall(task='write a flask jwt login', as_prompt=True)" },
  { kind: "out", text: "→ matched 4 lessons · 1 procedure (conf 0.91)", tone: "cyan" },
  { kind: "comment", text: "# 2. capture after execution" },
  { kind: "code", text: "m.capture(agent_id='coder-7', task=task, actions=trace," },
  { kind: "code", text: "          output=res, success=True, time_taken=2.4)" },
  { kind: "out", text: "✓ event 0x91A queued for reflection", tone: "green" },
  { kind: "out", text: "↳ lesson generated: 'Use rounds=12 for bcrypt' (conf 0.84)", tone: "amber" },
];

export default function TerminalDemo({ className = "" }) {
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [printed, setPrinted] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (lineIdx >= SCRIPT.length) {
      // Loop after a pause
      const t = setTimeout(() => {
        setPrinted([]);
        setLineIdx(0);
        setCharIdx(0);
      }, 3000);
      return () => clearTimeout(t);
    }
    const current = SCRIPT[lineIdx];
    if (charIdx < current.text.length) {
      const speed = current.kind === "out" ? 6 : 18;
      const t = setTimeout(() => setCharIdx(charIdx + 1), speed + Math.random() * 10);
      return () => clearTimeout(t);
    }
    // Line done
    const t = setTimeout(() => {
      setPrinted((p) => [...p, current]);
      setLineIdx(lineIdx + 1);
      setCharIdx(0);
    }, current.kind === "out" ? 220 : 90);
    return () => clearTimeout(t);
  }, [lineIdx, charIdx]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [printed, charIdx]);

  const colorOf = (kind, tone) => {
    if (tone === "cyan") return "text-cyber";
    if (tone === "amber") return "text-amber";
    if (tone === "green") return "text-terminal";
    if (kind === "comment") return "text-ink-500";
    if (kind === "prompt") return "text-ink-100";
    return "text-ink-300";
  };

  const current = SCRIPT[Math.min(lineIdx, SCRIPT.length - 1)];

  return (
    <div className={`border border-line-2 bg-surface ${className}`}>
      {/* Window chrome */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-line-1 bg-elevated">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-hotpink/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-terminal/60" />
        </div>
        <div className="text-[10px] tracking-[0.3em] text-ink-500">~/agents/coder-7 — zsh</div>
        <div className="text-[10px] tracking-[0.3em] text-cyber">REC ●</div>
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        className="font-mono text-[12px] md:text-[13px] leading-6 p-4 h-[320px] overflow-hidden scanlines"
        data-testid="terminal-demo-body"
      >
        {printed.map((l, i) => (
          <div key={i} className={`${colorOf(l.kind, l.tone)} whitespace-pre`}>
            {l.text}
          </div>
        ))}
        {lineIdx < SCRIPT.length && (
          <div className={`${colorOf(current.kind, current.tone)} whitespace-pre`}>
            {current.text.slice(0, charIdx)}
            <span className="text-terminal animate-blink">▌</span>
          </div>
        )}
      </div>
    </div>
  );
}
